import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import Preloader from '../Preloader';
import { getPageLoaderSnapshot, hasPageLoaderPages, subscribePageLoader } from './pageLoaderStore';
import waitForVisibleImages from './waitForVisibleImages';
import './PageLoader.css';

/** Once shown, the loader stays at least this long — a sub-frame logo flash reads as a glitch. */
const MIN_VISIBLE_MS = 400;
/** Cap on waiting for above-the-fold images after the data arrived. */
const IMAGE_WAIT_MS = 1500;
/** Safety net when `animationend` never fires (tab in background, animations disabled). */
const EXIT_FALLBACK_MS = 1000;
/** Safety net for a URL path change the router never commits (blocked / out-of-router write). */
const NAVIGATION_FALLBACK_MS = 5000;

/** The router-relevant path of the address bar (HashRouter builds keep it in `#/…`). */
const urlPath = () => {
  const { pathname, hash } = window.location;
  return hash.startsWith('#/') ? `${pathname}${hash.split('?')[0]}` : pathname;
};

/**
 * PageLoaderHost — full-viewport brand loader (logotype + DS `Preloader` line)
 * covering header, content and footer while a page is on its first load.
 * Mount ONCE in App, after the routes; pages opt in with `usePageLoader()`.
 *
 * Phases: hidden → shown (instant, opaque — the header must never paint
 * first) → leaving (logo lifts out, then the white curtain fades to reveal
 * the page) → hidden. The reveal waits for MIN_VISIBLE_MS and for the images
 * in the viewport, so the page appears complete in one move.
 */
const PageLoaderHost = () => {
  const [store, setStore] = useState(getPageLoaderSnapshot);
  const [phase, setPhase] = useState(() => (getPageLoaderSnapshot().active ? 'shown' : 'hidden'));
  const shownAtRef = useRef(phase === 'shown' ? performance.now() : 0);
  const rootRef = useRef(null);

  // Route changes render in a transition (`v7_startTransition`): the address
  // bar changes at once, but the old page stays on screen until the new route
  // commits — on a heavy page that is the old grid under the new URL for a
  // noticeable while, and the page's own hook only runs at that commit. So the
  // host watches the URL itself (history writes + popstate, all urgent) and
  // covers the screen the moment the PATH changes on a page that uses the
  // loader; the commit then hands over to the page's registration (or, for a
  // page without one, to the normal reveal). Query-only writes (`?class=`) are
  // ignored.
  const [navigating, setNavigating] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    let last = urlPath();
    const check = () => {
      const next = urlPath();
      if (next === last) return;
      last = next;
      if (hasPageLoaderPages()) setNavigating(true);
    };
    const wrap = (name) => {
      const original = window.history[name];
      window.history[name] = function wrappedHistoryWrite(...args) {
        const result = original.apply(this, args);
        check();
        return result;
      };
      return () => { window.history[name] = original; };
    };
    const unwrapPush = wrap('pushState');
    const unwrapReplace = wrap('replaceState');
    window.addEventListener('popstate', check);
    return () => {
      unwrapPush();
      unwrapReplace();
      window.removeEventListener('popstate', check);
    };
  }, []);

  // The router committed the new route: pages' layout effects (earlier in the
  // tree) have registered by now, so the store decides from here.
  useLayoutEffect(() => { setNavigating(false); }, [pathname]);

  useEffect(() => {
    if (!navigating) return undefined;
    const timer = setTimeout(() => setNavigating(false), NAVIGATION_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [navigating]);

  const active = store.active || navigating;

  // Layout-effect subscription: pages register in their own layout effects,
  // which run before this one in the same commit — `sync()` picks them up and
  // the re-render lands before the browser paints.
  useLayoutEffect(() => {
    const sync = () => setStore(getPageLoaderSnapshot());
    const unsubscribe = subscribePageLoader(sync);
    sync();
    return unsubscribe;
  }, []);

  useLayoutEffect(() => {
    if (active) {
      if (phase !== 'shown') {
        shownAtRef.current = performance.now();
        setPhase('shown');
      }
      return undefined;
    }
    if (phase === 'shown') {
      let cancelled = false;
      const dwell = Math.max(0, MIN_VISIBLE_MS - (performance.now() - shownAtRef.current));
      const timer = setTimeout(() => {
        waitForVisibleImages({ timeout: IMAGE_WAIT_MS, exclude: rootRef.current }).then(() => {
          if (!cancelled) setPhase('leaving');
        });
      }, dwell);
      return () => { cancelled = true; clearTimeout(timer); };
    }
    if (phase === 'leaving') {
      const timer = setTimeout(() => setPhase('hidden'), EXIT_FALLBACK_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [active, phase]);

  if (phase === 'hidden' || typeof document === 'undefined') return null;

  const handleAnimationEnd = (e) => {
    // Only the curtain's own fade ends the exit (the logo's animation bubbles too).
    if (phase === 'leaving' && e.target === e.currentTarget) setPhase('hidden');
  };

  return createPortal(
    <div
      ref={rootRef}
      className={`ds-page-loader${phase === 'leaving' ? ' ds-page-loader--leaving' : ''}`}
      onAnimationEnd={handleAnimationEnd}
    >
      <div className="ds-page-loader__content">
        <Preloader variant="logo" brand={store.brand} label={store.label} />
      </div>
    </div>,
    document.body,
  );
};

export default PageLoaderHost;
